/**
 * ReportSheet — 举报 / 投诉弹层（_shared C-2 举报入口；对齐 RN report-sheet/ReportSheet.tsx）。
 *
 * 三态入口（Story 30-3）：
 *  - PRODUCT：商品详情「⋯ 更多」→举报违规商品（targetId=商品 id）
 *  - SELLER ：商品详情卖家卡→举报卖家（targetId=卖家 user id）
 *  - PLATFORM：Footer 全局「举报」→平台级投诉（无 targetId，用户自由填理由）
 *
 * 构成：举报类型 6 项单选 + 说明 textarea（必填,≤200,实时计数）+ 证据区（选填,≤3 张,浏览器原生
 * File 走既有直传）+ 主按钮「提交举报」（类型未选/说明空/提交中/上传中 → disabled）。
 * 承载于桌面模态 Dialog（C-5：Esc/遮罩关闭、聚焦）；成功/错误轻反馈走 Toast（C-6），
 * 但业务错误（020301 24h 限报 / 020302 内容违规等）在 sheet 内原地提示、不关闭,供修改后重试。
 * 提交经 useAuthRequired（≥REGISTERED）前置把关（入口处包裹）。
 */
import { useEffect, useRef, useState } from 'react';
import { ServerError } from '@/api/client';
import {
  REPORT_DESC_MAX,
  REPORT_SCREENSHOT_MAX,
  REPORT_TYPES,
  submitReport,
  uploadReportEvidence,
  type ReportTargetTypeCode,
  type ReportTypeCode,
} from '@/api/report';
import { Dialog } from '@/components/ui/Dialog';
import { toast } from '@/components/ui/toast';

export interface ReportSheetProps {
  open: boolean;
  /** 举报对象类型：PRODUCT / SELLER / PLATFORM。 */
  targetType: ReportTargetTypeCode;
  /** 被举报对象 id：PRODUCT=商品 id / SELLER=卖家 user id；PLATFORM 省略。 */
  targetId?: string;
  /** 关闭（✕ / 遮罩 / Esc / 提交成功）。 */
  onClose: () => void;
  /** 提交成功回调（可选，父组件可用于埋点 / 刷新）。 */
  onSubmitted?: () => void;
}

const ACCEPT_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

/** 标题与说明占位随对象类型区分。 */
function copyFor(targetType: ReportTargetTypeCode): { title: string; placeholder: string } {
  switch (targetType) {
    case 'SELLER':
      return { title: '举报卖家', placeholder: '请描述该卖家的违规情况' };
    case 'PLATFORM':
      return { title: '举报 / 投诉平台', placeholder: '请描述您要投诉的平台问题（我们会尽快处理并可能回访）' };
    case 'PRODUCT':
    default:
      return { title: '举报违规商品', placeholder: '请描述该商品的违规情况' };
  }
}

/** 业务错误码 → sheet 内提示文案（原地展示,不关闭）。 */
const SUBMIT_ERROR_MSG: Record<string, string> = {
  '020301': '24 小时内对同一对象仅可举报一次',
  '020302': '举报内容含违规信息，请修改后重试',
  '020303': '举报类型无效',
  '020304': '举报对象类型无效',
  '020001': '被举报商品不存在',
  '020003': '被举报商品已下架',
  '020305': '被举报卖家不存在',
  '020306': '不能举报自己',
};

export function ReportSheet({ open, targetType, targetId, onClose, onSubmitted }: ReportSheetProps) {
  const { title, placeholder } = copyFor(targetType);

  const [type, setType] = useState<ReportTypeCode | null>(null);
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // 同步防重入哨兵：submitting state 异步生效，两次极快点击会在 re-render 前都穿过 disabled 闭包。
  const submittingRef = useRef(false);

  // 关闭时复位（再次打开是干净状态）
  useEffect(() => {
    if (!open) {
      setType(null);
      setDescription('');
      setScreenshots([]);
      setUploading(false);
      setSubmitting(false);
      submittingRef.current = false;
      setErrorMsg('');
    }
  }, [open]);

  const descLen = description.length;
  const submitDisabled =
    type == null || description.trim().length === 0 || submitting || uploading;

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选同一文件
    if (!file) return;
    if (screenshots.length >= REPORT_SCREENSHOT_MAX) {
      toast.info(`最多上传 ${REPORT_SCREENSHOT_MAX} 张证据图`);
      return;
    }
    if (!ACCEPT_IMAGE_TYPES.includes(file.type)) {
      toast.error('仅支持 JPG / PNG 图片');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('单张图片不超过 5MB');
      return;
    }
    setUploading(true);
    setErrorMsg('');
    try {
      const url = await uploadReportEvidence(file);
      setScreenshots((prev) => (prev.includes(url) ? prev : [...prev, url].slice(0, REPORT_SCREENSHOT_MAX)));
    } catch {
      setErrorMsg('证据图上传失败，请重新选图');
    } finally {
      setUploading(false);
    }
  };

  const removeScreenshot = (idx: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (submittingRef.current || submitDisabled || type == null) return;
    submittingRef.current = true;
    setSubmitting(true);
    setErrorMsg('');
    try {
      await submitReport({
        targetType,
        targetId,
        type,
        description: description.trim(),
        screenshots,
      });
      toast.success('举报已提交');
      onSubmitted?.();
      onClose();
    } catch (err) {
      const code = err instanceof ServerError ? err.code : undefined;
      setErrorMsg(code ? (SUBMIT_ERROR_MSG[code] ?? '提交失败，请稍后重试') : '提交失败，请稍后重试');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} labelledBy="report-sheet-title">
      <div className="row between mb-4">
        <h3 className="h3" id="report-sheet-title">
          {title}
        </h3>
        <button type="button" className="icon-btn" aria-label="关闭" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* 举报类型 6 项单选 */}
      <div className="field">
        <label className="label">举报类型</label>
        <div className="row gap-2" role="radiogroup" aria-label="举报类型" style={{ flexWrap: 'wrap' }}>
          {REPORT_TYPES.map((t) => (
            <button
              key={t.code}
              type="button"
              role="radio"
              aria-checked={type === t.code}
              className={type === t.code ? 'btn btn-primary sm' : 'btn btn-secondary sm'}
              onClick={() => setType(t.code)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 说明 */}
      <div className="field">
        <label className="label" htmlFor="report-desc">
          说明(必填)
        </label>
        <textarea
          className="textarea"
          id="report-desc"
          maxLength={REPORT_DESC_MAX}
          placeholder={placeholder}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="row between">
          <p className="hint">请如实描述，便于平台核实处理</p>
          <p className="hint">
            {descLen} / {REPORT_DESC_MAX}
          </p>
        </div>
      </div>

      {/* 证据（选填，最多 3 张） */}
      <div className="field">
        <label className="label">证据(选填,最多 {REPORT_SCREENSHOT_MAX} 张)</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'var(--space-3)',
            maxWidth: 360,
          }}
        >
          {screenshots.map((url, idx) => (
            <div className="review-thumb" key={url}>
              <img src={url} alt={`证据图 ${idx + 1}`} />
              <button
                type="button"
                className="review-thumb-del"
                aria-label="删除证据图"
                onClick={() => removeScreenshot(idx)}
              >
                ×
              </button>
            </div>
          ))}
          {screenshots.length < REPORT_SCREENSHOT_MAX && (
            <label className="upload-box">
              <span>{uploading ? '上传中…' : '+ 上传'}</span>
              <input type="file" accept="image/*" hidden disabled={uploading} onChange={onPickFile} />
            </label>
          )}
        </div>
      </div>

      {errorMsg && (
        <p className="hint" role="alert" style={{ color: 'var(--color-destructive)' }}>
          {errorMsg}
        </p>
      )}

      <button
        type="button"
        className="btn btn-primary btn-lg btn-block mt-4"
        disabled={submitDisabled}
        onClick={handleSubmit}
      >
        {submitting ? '提交中…' : '提交举报'}
      </button>
    </Dialog>
  );
}
